import {TrackDTO} from '../types';
import {MEDIA_SEEK_OFFSET_SEC} from '../constants';

export interface MediaSessionActions {
    play(): void;
    pause(): void;
    stop(): void;
    next(): void;
    previous(): void;
    seekTo(positionSeconds: number): void;
    getPosition(): number;
    getDuration(): number;
}

type PlaybackState = 'playing' | 'paused' | 'none';

export class MediaSessionController {
    private readonly supported: boolean =
        typeof navigator !== 'undefined' && 'mediaSession' in navigator;

    constructor(private readonly actions: MediaSessionActions) {}

    attachHandlers(): void {
        if (!this.supported) return;

        navigator.mediaSession.setActionHandler('play', () => this.actions.play());
        navigator.mediaSession.setActionHandler('pause', () => {
            this.actions.pause();
            this.setPlaybackState('paused');
        });
        navigator.mediaSession.setActionHandler('stop', () => {
            this.actions.pause();
            this.setPlaybackState('none');
        });
        navigator.mediaSession.setActionHandler('nexttrack', () => this.actions.next());
        navigator.mediaSession.setActionHandler('previoustrack', () => this.actions.previous());
        navigator.mediaSession.setActionHandler('seekforward', details => {
            this.seekBy(details.seekOffset ?? MEDIA_SEEK_OFFSET_SEC);
        });
        navigator.mediaSession.setActionHandler('seekbackward', details => {
            this.seekBy(-(details.seekOffset ?? MEDIA_SEEK_OFFSET_SEC));
        });
        navigator.mediaSession.setActionHandler('seekto', details => {
            if (typeof details.seekTime === 'number') {
                this.seekTo(details.seekTime);
            }
        });
    }

    setMetadata(track: TrackDTO): void {
        if (!this.supported) return;

        navigator.mediaSession.metadata = new MediaMetadata({
            title: track.title,
            artist: track.artist,
            artwork: track.cover ? [{ src: `/api/files/${track.cover}` }] : [],
        });
    }

    setPlaybackState(state: PlaybackState): void {
        if (!this.supported) return;

        navigator.mediaSession.playbackState = state;
        this.updatePositionState();
    }

    updatePositionState(): void {
        if (!this.supported) return;

        const duration = this.actions.getDuration();
        const position = this.actions.getPosition();

        if (!Number.isFinite(duration) || duration <= 0) return;

        navigator.mediaSession.setPositionState({
            duration,
            position: Math.min(Math.max(position, 0), duration),
            playbackRate: 1,
        });
    }

    dispose(): void {
        if (!this.supported) return;

        const actions = [
            'play',
            'pause',
            'stop',
            'nexttrack',
            'previoustrack',
            'seekforward',
            'seekbackward',
            'seekto'
        ] as MediaSessionAction[];

        actions.forEach(action => navigator.mediaSession.setActionHandler(action, null));
    }

    private seekBy(offsetSeconds: number): void {
        this.seekTo(this.actions.getPosition() + offsetSeconds);
    }

    private seekTo(positionSeconds: number): void {
        this.actions.seekTo(positionSeconds);
        this.updatePositionState();
    }
}
